// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { messages, useMessages } from '../i18n.js';
import { useEffect, useRef, useState } from 'react';

export type VoicePhase = 'off' | 'recording' | 'transcribing';

/**
 * A press shorter than this is a tap; at or beyond it, a hold. Hold to talk and release to send is
 * what the founder asked for (2026-09-11, item 5) and what every messaging app taught; the tap is
 * kept because a keyboard cannot hold, and because a person who wants to read their words before
 * sending them should have a way to — tap to start, tap again to stop, and the words land in the box.
 */
export const HOLD_TO_SEND_MS = 250;
/** Sliding the held pointer this far up discards the recording, as the hint under it says. */
export const SWIPE_TO_CANCEL_PX = 80;
/**
 * How much the person wants said back — user words only; the mapping lives server-side. The three
 * chips under the box went on 2026-09-20 (founder: "use Balanced as default and hide others"), so
 * every turn is balanced and nothing here renders a choice. The type stays because the wire field
 * stays; `App` sends the one value.
 */
export type DepthMode = 'quick' | 'balanced' | 'deep';

const T = messages({
  ar: {
    listening: 'بلال يستمع…',
    connecting: 'جارٍ الاتصال…',
    hearingYou: 'أسمعك…',
    dragToCancel: 'اسحب للأعلى للإلغاء ·',
    releaseToSend: 'اترك للإرسال',
    prompt: 'ما الذي تريد التفكير فيه؟',
    send: 'إرسال',
    voiceMode: 'محادثة صوتية',
    stopRecording: 'إيقاف التسجيل',
    speak: 'تحدّث',
    holdToSpeak: 'اضغط مطوّلًا وتحدّث، ثم أفلت للإرسال',
    cancelRecording: 'إلغاء التسجيل',
    liveRecording: 'تسجيل مباشر',
    recording: 'جارٍ التسجيل…',
    releaseOrDrag: 'اترك للإرسال · اسحب للأعلى للإلغاء',
    stopOrCancel: 'اضغط على الإيقاف عند الانتهاء، أو على الإلغاء',
    transcribing: 'جارٍ تحويل الصوت إلى نص…',
    keyboardHint: 'Enter للإرسال · Shift+Enter لسطر جديد',
    holdHint: 'اضغط مطوّلًا على الميكروفون وتحدّث، ثم أفلت للإرسال',
  },
  en: {
    listening: 'Bilal is listening…',
    connecting: 'Connecting…',
    hearingYou: 'I can hear you…',
    dragToCancel: 'Slide up to cancel ·',
    releaseToSend: 'Release to send',
    prompt: 'What would you like to think about?',
    send: 'Send',
    voiceMode: 'Voice conversation',
    stopRecording: 'Stop recording',
    speak: 'Speak',
    holdToSpeak: 'Press and hold to speak, then release to send',
    cancelRecording: 'Cancel the recording',
    liveRecording: 'Recording live',
    recording: 'Recording…',
    releaseOrDrag: 'Release to send · slide up to cancel',
    stopOrCancel: 'Press stop when you are done, or cancel to discard it',
    transcribing: 'Turning the audio into text…',
    keyboardHint: 'Enter to send · Shift+Enter for a new line',
    holdHint: 'press and hold the microphone and speak, then release to send',
  },
});

export interface LiveVoiceView {
  readonly committed: string;
  readonly interim: string;
  /** Real microphone loudness from the capture pipeline; never decorative. */
  readonly level: number;
  readonly startedAt: number;
  /** False while the socket and provider are still connecting. */
  readonly live: boolean;
}

/** The recording clock, from the moment the session actually went live. */
function LiveTimer({ startedAt }: { readonly startedAt: number }): React.JSX.Element {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  const mm = String(Math.floor(seconds / 60));
  const ss = String(seconds % 60).padStart(2, '0');
  return <span className="live-timer">{`${mm}:${ss}`}</span>;
}

/** Five bars scaled by the real captured level — a meter, not an animation. */
function LevelMeter({ level }: { readonly level: number }): React.JSX.Element {
  const shaped = Math.max(0.08, Math.min(1, level));
  const bars = [0.55, 0.8, 1, 0.8, 0.55];
  return (
    <span className="level-meter" aria-hidden="true">
      {bars.map((weight, index) => (
        <span
          key={index}
          style={{ transform: `scaleY(${String(0.15 + shaped * weight * 0.85)})` }}
        />
      ))}
    </span>
  );
}

/**
 * The microphone and the stop square, drawn rather than typed. They used to be the emoji characters
 * 🎙 and ⏹, which every desktop platform renders as a full-colour picture: the control sets a
 * colour and the glyph ignores it, so the composer's largest element — the 80px microphone on the
 * home screen — arrived in someone else's palette. An inline path takes `currentColor` and belongs
 * to the design (founder, 2026-09-13, on the phone especially).
 */
function MicIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="glyph" aria-hidden="true" focusable="false">
      <path
        d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function StopIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="glyph" aria-hidden="true" focusable="false">
      <rect x="7" y="7" width="10" height="10" rx="2.2" fill="currentColor" />
    </svg>
  );
}

/**
 * The sticky composer: a rounded container with an auto-growing textarea, the send control and the
 * microphone. Enter sends and Shift+Enter breaks the line, which is the grammar every chat product
 * taught; sending with nothing to say is disabled rather than refused after the fact.
 *
 * DOM order is keyboard order — textarea first, then send — while the stylesheet's `order` lays the
 * controls out visually around the box.
 *
 * The microphone is one control with two grammars. Hold it and speak, release, and the words go as
 * the turn when the transcription is sure of them (a doubtful transcription lands in the box with a
 * hint instead, so a person never sends words they did not say). Tap it and the recording runs until
 * the next tap, and the words land in the box for correction — the path a keyboard user gets, since
 * Enter and Space cannot hold. It is the same DOM node in both states: a hold in progress must not
 * lose its pointer to a control that unmounted under it.
 */
export function Composer({
  draft,
  onDraftChange,
  onSend,
  sending,
  voice,
  canRecord,
  onStartRecording,
  onStopRecording,
  onReleaseToSend,
  onCancelRecording,
  hint = null,
  holdToSendMs = HOLD_TO_SEND_MS,
  speakButton,
  stopButton,
  boxRef,
  live,
  onEnterVoiceMode,
}: {
  readonly draft: string;
  readonly onDraftChange: (text: string) => void;
  readonly onSend: () => void;
  readonly sending: boolean;
  readonly voice: VoicePhase;
  readonly canRecord: boolean;
  readonly onStartRecording: () => void;
  /** A deliberate stop: the words land in the box for review. */
  readonly onStopRecording: () => void;
  /** The hold was released: the words are sent as the turn when the transcription is sure of them. */
  readonly onReleaseToSend: () => void;
  readonly onCancelRecording: () => void;
  /** A short status line under the box — why a transcript landed for review, for instance. */
  readonly hint?: string | null;
  /** How long a press must last to count as a hold rather than a tap. Exposed for tests. */
  readonly holdToSendMs?: number;
  readonly speakButton: React.RefObject<HTMLButtonElement | null>;
  readonly stopButton: React.RefObject<HTMLButtonElement | null>;
  /** The textarea itself, so the parent can hand it the keyboard after a transcript lands. */
  readonly boxRef?: React.RefObject<HTMLTextAreaElement | null>;
  /** The realtime transcription in progress, or null when recording is off or HTTP-based. */
  readonly live?: LiveVoiceView | null | undefined;
  /** Opens the hands-free live voice conversation; absent when realtime voice is unavailable. */
  readonly onEnterVoiceMode?: (() => void) | undefined;
}): React.JSX.Element {
  const t = useMessages(T);
  const textarea = useRef<HTMLTextAreaElement>(null);

  // Auto-grow: the box fits its text up to the stylesheet's max-height, then scrolls.
  useEffect(() => {
    const box = textarea.current;
    if (box === null) return;
    box.style.height = 'auto';
    box.style.height = `${String(box.scrollHeight)}px`;
  }, [draft]);

  const empty = draft.trim().length === 0;

  // When the press began, or null when no press is in progress. A ref, not state: the pointer
  // sequence must not be re-rendered in the middle.
  const pressedAt = useRef<number | null>(null);
  const pressStartY = useRef<number | null>(null);
  /**
   * Which pointer owns the press, so a press whose release never arrived can be told from a fresh
   * one (founder, 2026-09-20: "sometimes stuck and we cannot close it").
   */
  const pressPointer = useRef<number | null>(null);
  /** True while a press is in progress: the status line says what releasing will do. */
  const [holding, setHolding] = useState(false);
  /** When the current recording began, for the timer; null when nothing is recording. */
  const [recordingSince, setRecordingSince] = useState<number | null>(null);
  useEffect(() => {
    setRecordingSince(voice === 'recording' ? Date.now() : null);
    if (voice !== 'recording' && voice !== 'off') setHolding(false);
  }, [voice]);
  // The keyboard's way out of a recording that will not end. A phone has the tap and the slide; a
  // desktop had neither once the pointer was lost, and Escape is what a person tries first.
  useEffect(() => {
    if (voice !== 'recording') return undefined;
    const doc = globalThis.document;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      pressedAt.current = null;
      pressStartY.current = null;
      pressPointer.current = null;
      setHolding(false);
      onCancelRecording();
    };
    doc.addEventListener('keydown', onKey);
    return () => {
      doc.removeEventListener('keydown', onKey);
    };
  }, [voice, onCancelRecording]);
  const endPress = (): void => {
    pressedAt.current = null;
    pressStartY.current = null;
    pressPointer.current = null;
    setHolding(false);
  };
  const micPointerDown = (event: React.PointerEvent<HTMLButtonElement>): void => {
    // A right or middle button is not a press to talk.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // A press whose release never arrived. iOS takes a pointer away for a system gesture, a finger
    // leaves the top of the screen, a capture is broken by a re-render — and the old code left
    // `pressedAt` set for ever after, which made the overlay permanent *and* made every later tap a
    // no-op, because the tap-to-stop branch below required no press to be in progress. There was
    // then no way out but reloading the page. A press from a different pointer ends the stale one.
    if (pressedAt.current !== null && pressPointer.current !== event.pointerId) {
      endPress();
      if (voice === 'recording') onStopRecording();
      return;
    }
    if (voice === 'off') {
      pressedAt.current = Date.now();
      pressStartY.current = event.clientY;
      pressPointer.current = event.pointerId;
      setHolding(true);
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // No pointer capture (older browsers, test DOMs): the release still lands while the pointer
        // stays on the control, which is where a thumb holding a button is.
      }
      onStartRecording();
      return;
    }
    // A second tap on a recording a tap started: a deliberate stop, to review.
    if (voice === 'recording' && pressedAt.current === null) onStopRecording();
  };
  const micPointerUp = (): void => {
    const started = pressedAt.current;
    if (started === null) return;
    endPress();
    if (Date.now() - started >= holdToSendMs) onReleaseToSend();
    // A tap: the recording continues until the next tap.
  };
  const micPointerMove = (event: React.PointerEvent<HTMLButtonElement>): void => {
    const startY = pressStartY.current;
    if (pressedAt.current === null || startY === null) return;
    if (startY - event.clientY < SWIPE_TO_CANCEL_PX) return;
    // Slid up and away: the words are discarded, exactly as the hint under the control promised.
    endPress();
    onCancelRecording();
  };
  /**
   * The browser took the capture back without a release — the element re-rendered, or the gesture
   * was claimed elsewhere. After an ordinary release this fires with no press in progress and does
   * nothing; it is the lost-release case it exists for.
   */
  const micLostCapture = (): void => {
    if (pressedAt.current === null) return;
    endPress();
    onCancelRecording();
  };
  const micPointerCancel = (): void => {
    // The browser took the pointer away mid-hold (a scroll, a system gesture): nothing was released
    // on purpose, so nothing is sent.
    if (pressedAt.current === null) return;
    endPress();
    onCancelRecording();
  };
  const micKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // The keyboard's grammar is the tap's: a key cannot hold. Preventing the default also stops the
    // synthetic click a key press would otherwise raise.
    event.preventDefault();
    if (event.repeat) return;
    if (voice === 'off') onStartRecording();
    else if (voice === 'recording') onStopRecording();
  };
  const recording = voice === 'recording';
  // The full-screen hold state (redesign canvas): the beam, the pill, the live words large, the
  // hint and the timer. Presentation only — the inline status line beneath stays for assistive
  // technology, and the overlay takes no pointer, so the release still lands on the microphone.
  const holdOverlay =
    holding && recording ? (
      <div className="hold-overlay" aria-hidden="true">
        <div className="hold-beam" />
        <p className="hold-pill">{t.listening}</p>
        <p className="hold-transcript" dir="auto">
          {live != null && live.committed.length > 0 && (
            <span className="hold-committed">{live.committed}</span>
          )}
          {live != null && live.interim.length > 0 && (
            <span className="hold-interim"> {live.interim}</span>
          )}
          {(live == null || (live.committed.length === 0 && live.interim.length === 0)) && (
            <span className="hold-waiting">
              {live != null && !live.live ? t.connecting : t.hearingYou}
            </span>
          )}
        </p>
        <p className="hold-hint">
          {t.dragToCancel} <LiveTimer startedAt={live?.startedAt ?? recordingSince ?? Date.now()} />
        </p>
        <p className="hold-release">{t.releaseToSend}</p>
      </div>
    ) : null;
  return (
    <div className="composer-region">
      {holdOverlay}
      <div className="chat-column">
        <div className="composer">
          <label htmlFor="transcript" className="visually-hidden">
            {t.prompt}
          </label>
          <textarea
            id="transcript"
            ref={(node) => {
              textarea.current = node;
              if (boxRef !== undefined) boxRef.current = node;
            }}
            placeholder={t.prompt}
            rows={1}
            value={draft}
            onChange={(event) => {
              onDraftChange(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                if (!empty) onSend();
              }
            }}
          />
          <button
            type="button"
            className="icon-button send"
            onClick={onSend}
            disabled={empty}
            aria-label={t.send}
            title={t.send}
          >
            ↑
          </button>
          {onEnterVoiceMode !== undefined && voice === 'off' && (
            <button
              type="button"
              className="icon-button voice-mode-entry"
              onClick={onEnterVoiceMode}
              disabled={sending}
              aria-label={t.voiceMode}
              title={t.voiceMode}
            >
              ◉
            </button>
          )}
          {canRecord && voice !== 'transcribing' && (
            <button
              type="button"
              className={
                recording
                  ? holding
                    ? 'icon-button mic recording holding'
                    : 'icon-button mic recording'
                  : 'icon-button mic'
              }
              ref={(node) => {
                // One node wears both names, so focus handed to either lands on the control.
                speakButton.current = node;
                stopButton.current = node;
              }}
              disabled={!recording && sending}
              aria-label={recording ? t.stopRecording : t.speak}
              title={recording ? t.stopRecording : t.holdToSpeak}
              onPointerDown={micPointerDown}
              onPointerUp={micPointerUp}
              onPointerMove={micPointerMove}
              onPointerCancel={micPointerCancel}
              onLostPointerCapture={micLostCapture}
              onKeyDown={micKeyDown}
              onContextMenu={(event) => {
                // A long press on a touch screen must not open a menu over the recording.
                event.preventDefault();
              }}
            >
              {recording ? <StopIcon /> : <MicIcon />}
            </button>
          )}
          {canRecord && recording && (
            <button
              type="button"
              className="icon-button mic-cancel"
              onClick={onCancelRecording}
              aria-label={t.cancelRecording}
              title={t.cancelRecording}
            >
              ✕
            </button>
          )}
        </div>
        {voice === 'recording' && live != null && (
          <div className="live-region">
            <p className="live-transcript" dir="auto" aria-live="polite">
              {live.committed.length > 0 && (
                <span className="live-committed">{live.committed}</span>
              )}
              {live.interim.length > 0 && <span className="live-interim">{live.interim}</span>}
              {live.committed.length === 0 && live.interim.length === 0 && (
                <span className="live-waiting">{live.live ? t.hearingYou : t.connecting}</span>
              )}
            </p>
            <p role="status" className="voice-status live-status">
              <span className="rec-dot" aria-hidden="true" />
              {live.live ? (holding ? t.listening : t.liveRecording) : t.connecting}
              {live.live && <LiveTimer startedAt={live.startedAt} />}
              {live.live && <LevelMeter level={live.level} />}
            </p>
            <p className="voice-hint-line">{holding ? t.releaseOrDrag : t.stopOrCancel}</p>
          </div>
        )}{' '}
        {voice === 'recording' && live == null && (
          <div className="record-state">
            <p role="status" className="voice-status live-status">
              <span className="rec-dot" aria-hidden="true" />
              {holding ? t.listening : t.recording}
              {recordingSince !== null && <LiveTimer startedAt={recordingSince} />}
            </p>
            <p className="voice-hint-line">{holding ? t.releaseOrDrag : t.stopOrCancel}</p>
          </div>
        )}
        {voice === 'transcribing' && (
          <p role="status" className="voice-status">
            {t.transcribing}
          </p>
        )}
        {voice === 'off' && hint !== null && (
          <p role="status" className="voice-status">
            {hint}
          </p>
        )}
        {/* Two hints, not one sentence: a phone has no Enter key and no Shift, so the keyboard half
            is hidden where a coarse pointer says the keyboard is a glass one. The separator went
            with it, which is why each half is now its own clean string. */}
        <p className="composer-hints">
          <span className="hint-keys">{t.keyboardHint}</span>
          {canRecord && <span className="hint-hold">{t.holdHint}</span>}
        </p>
      </div>
    </div>
  );
}
